import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';

const LogsInsights = () => {
  const [activeTab, setActiveTab] = useState('logs');
  const [timeRange, setTimeRange] = useState('24h');
  
  const logs = [
    {
      id: 1,
      timestamp: '2024-02-02 14:32:15',
      level: 'INFO',
      service: 'Customer Support AI',
      message: 'Successfully processed ticket #CS-2024-001',
      user: 'sarah.chen@orbitmind.ai'
    },
    {
      id: 2,
      timestamp: '2024-02-02 14:28:42',
      level: 'WARNING',
      service: 'Sales Assistant',
      message: 'Rate limit approaching for Salesforce API',
      user: 'system'
    },
    {
      id: 3,
      timestamp: '2024-02-02 14:25:18',
      level: 'ERROR',
      service: 'DevOps Bot',
      message: 'Failed to deploy to staging environment',
      user: 'marcus.thorne@orbitmind.ai'
    },
    {
      id: 4,
      timestamp: '2024-02-02 14:20:33',
      level: 'INFO',
      service: 'HR Automator',
      message: 'Onboarding workflow completed for new employee',
      user: 'hr.system@orbitmind.ai'
    }
  ];

  const metrics = [
    { name: 'Total Requests', value: '12,847', change: '+12%', trend: 'up' },
    { name: 'Success Rate', value: '98.2%', change: '+0.3%', trend: 'up' },
    { name: 'Avg Response Time', value: '245ms', change: '-15ms', trend: 'down' },
    { name: 'Active Agents', value: '8', change: '+2', trend: 'up' }
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'text-red-600 bg-red-100 border-red-200';
      case 'WARNING': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'INFO': return 'text-blue-600 bg-blue-100 border-blue-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Logs & Insights</h1>
            <p className="text-gray-400">Monitor system performance and analyze AI agent activities</p>
          </div>
          <div className="flex items-center gap-4">
            <select 
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-1 focus:ring-primary outline-none"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
            <button className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm font-bold">
              Export Data
            </button>
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <div key={index} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-400">{metric.name}</p>
                  <p className="text-2xl font-bold text-white mt-1">{metric.value}</p>
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  metric.trend === 'up' ? 'text-green-500' : 'text-red-500'
                }`}>
                  <span className="text-sm">
                    {metric.trend === 'up' ? '↗️' : '↘️'}
                  </span>
                  {metric.change}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex border-b border-gray-700">
            {['logs', 'insights', 'performance'].map((tab) => (
              <button
                key={tab}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'logs' && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {/* Filters */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <select className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white">
                  <option>All Levels</option>
                  <option>ERROR</option>
                  <option>WARNING</option>
                  <option>INFO</option>
                </select>
                <select className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white">
                  <option>All Services</option>
                  <option>Customer Support AI</option>
                  <option>Sales Assistant</option>
                  <option>DevOps Bot</option>
                </select>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                <input 
                  className="bg-gray-700 border-none rounded-lg pl-9 pr-4 py-2 text-sm text-white w-64 focus:ring-1 focus:ring-primary outline-none" 
                  placeholder="Search logs..." 
                  type="text"
                />
              </div>
            </div>

            {/* Logs Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Level</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Service</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">Message</th>
                    <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {log.timestamp}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded border ${getLevelColor(log.level)}`}>
                          {log.level}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {log.service}
                      </td>
                      <td className="px-6 py-4 text-sm text-white">
                        {log.message}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {log.user}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <p className="text-sm text-gray-300">
                Showing 1 to {logs.length} of {logs.length} results
              </p>
              <div className="flex gap-2">
                <button className="px-3 py-1 text-sm font-medium text-gray-400 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50" disabled>
                  Previous
                </button>
                <button className="px-3 py-1 text-sm font-medium text-gray-400 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600">
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Usage Patterns */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-bold mb-4 text-white">Usage Patterns</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Peak Usage Hours</span>
                  <span className="text-sm font-medium text-white">9 AM - 5 PM</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Most Active Agent</span>
                  <span className="text-sm font-medium text-white">Customer Support AI</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Average Session Duration</span>
                  <span className="text-sm font-medium text-white">4.2 minutes</span>
                </div>
              </div>
            </div>

            {/* Error Analysis */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-bold mb-4 text-white">Error Analysis</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">API Timeouts</span>
                  <span className="text-sm font-medium text-red-500">12 occurrences</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Authentication Failures</span>
                  <span className="text-sm font-medium text-red-500">3 occurrences</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Rate Limit Exceeded</span>
                  <span className="text-sm font-medium text-yellow-500">8 occurrences</span>
                </div>
              </div>
            </div>

            {/* Performance Trends */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 lg:col-span-2">
              <h3 className="text-lg font-bold mb-4 text-white">Performance Trends</h3>
              <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-600 rounded-lg">
                <div className="text-center">
                  <span className="text-4xl text-gray-400 mb-2">📈</span>
                  <p className="text-gray-400">Performance chart visualization would go here</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Response Time Distribution */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-bold mb-4 text-white">Response Time Distribution</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">&lt; 100ms</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-600 rounded-full">
                      <div className="w-16 h-2 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-sm text-white">80%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">100-500ms</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-600 rounded-full">
                      <div className="w-3 h-2 bg-yellow-500 rounded-full"></div>
                    </div>
                    <span className="text-sm text-white">15%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">&gt; 500ms</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-600 rounded-full">
                      <div className="w-1 h-2 bg-red-500 rounded-full"></div>
                    </div>
                    <span className="text-sm text-white">5%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Performance */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-bold mb-4 text-white">Agent Performance</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Customer Support</span>
                  <span className="text-sm font-medium text-green-500">98.5%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Sales Assistant</span>
                  <span className="text-sm font-medium text-green-500">97.2%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">DevOps Bot</span>
                  <span className="text-sm font-medium text-yellow-500">94.8%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">HR Automator</span>
                  <span className="text-sm font-medium text-green-500">99.1%</span>
                </div>
              </div>
            </div>

            {/* System Health */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-bold mb-4 text-white">System Health</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">CPU Usage</span>
                  <span className="text-sm font-medium text-white">45%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Memory Usage</span>
                  <span className="text-sm font-medium text-white">62%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Disk Usage</span>
                  <span className="text-sm font-medium text-white">38%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Network I/O</span>
                  <span className="text-sm font-medium text-white">Normal</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsInsights;